<template>
  <div :class="[bemClass(), hasValueClass, focusClass, errClass, autoFillClass, $attrs.class]" :style="$attrs.style">
    <label :for="inputId" :class="bemClass('label')">
      <slot></slot>
    </label>
    <textarea
      v-if="isTextarea"
      :id="inputId"
      :name="name"
      :rows="lines"
      :value="modelValue"
      @blur="blur"
      @focus="focus"
      @input="input"
      @change="change"
      v-bind="attrs"
      :class="bemClass('textarea')"
    ></textarea>
    <input
      v-else
      type="text"
      :id="inputId"
      :name="name"
      :value="modelValue"
      @blur="blur"
      @focus="focus"
      @input="input"
      @change="change"
      v-bind="attrs"
      @animationstart="checkAnimation($event)"
      :class="bemClass('input')">
    <template v-if="!isValid && validateMessage">
      <transition name="fade">
        <p :class="bemClass('error')">{{validateMessage}}</p>
      </transition>
    </template>
  </div>
</template>

<script>
import { computed, ref, toRefs, watch } from 'vue'
import useBem from '../../compositions/bem'
import useAttrs from '../../compositions/use-attrs'
import { selGenUniqueId } from '../../util/util'

export default {
  name: 'SelTextInput',
  inheritAttrs: false,
  emits: ['update:modelValue'],
  props: {
    modelValue: {
      type: [String, Number]
    },
    lines: {
      type: Number,
      default: 1
    },
    name: {
      type: String,
      default: ''
    },
    lazy: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: false
    },
    validateFn: Function,
    errorMessage: {
      type: String,
      default: ''
    },
    errorClass: {
      type: String,
      default: 'has-error'
    },
    modifier: {
      type: String,
      default: ''
    }
  },
  setup (props, { emit }) {
    const { modelValue, modifier } = toRefs(props)
    const attrs = useAttrs()
    const { bemClass } = useBem('sel-text-input', modifier)
    const inputId = `input-${selGenUniqueId()}`
    const isTextarea = computed(() => parseInt(props.lines) > 1)
    const hasValue = computed(() => (props.modelValue && props.modelValue.length !== 0))
    const isFocused = ref(false)
    const isAutoFill = ref(false)
    const isValid = ref(true)
    const checkAnimation = (event) => {
      if (event.animationName.includes('onAutoFillStart')) {
        isAutoFill.value = true
      } else if (event.animationName.includes('onAutoFillCancel')) {
        isAutoFill.value = false
      }
    }

    const hasValueClass = computed(() => hasValue.value ? 'has-value' : '')
    const focusClass = computed(() => isFocused.value ? 'is-focused' : '')
    const autoFillClass = computed(() => isAutoFill.value ? 'is-auto-fill' : '')
    const validateMessage = computed(() => props.errorMessage ? props.errorMessage : 'Field is required')
    const errClass = computed(() => isValid.value ? '' : props.errorClass)

    watch(modelValue, () => {
      validate()
    })

    const validate = () => {
      const value = modelValue.value
      if (props.required) {
        isValid.value = !!value
      }
      if (props.validateFn) {
        isValid.value = props.validateFn(value)
      }
      return isValid.value
    }

    function input ($event) {
      if (!props.lazy) {
        change($event)
      }
    }

    function change ($event) {
      emit('update:modelValue', $event.target.value)
    }

    const focus = () => {
      isFocused.value = true
    }

    const blur = () => {
      isFocused.value = false
      validate()
    }

    return {
      attrs,
      bemClass,
      input,
      change,
      focus,
      blur,
      inputId,
      isFocused,
      hasValueClass,
      focusClass,
      isTextarea,
      isValid,
      validateMessage,
      validate,
      errClass,
      checkAnimation,
      isAutoFill,
      autoFillClass
    }
  }
}
</script>

<style scoped>
  .sel-text-input input,
  .sel-text-input textarea {
    appearance: none;
    min-height: 40px;
    padding: 10px;
    border: 1px solid #000;
    border-radius: 2px;
    color: #000;
    box-sizing: border-box;
  }

  .sel-text-input input:-webkit-autofill {
    animation-name: onAutoFillStart;
    transition: background-color 50000s ease-in-out 0s;
  }

  .sel-text-input input:not(:-webkit-autofill) {
    animation-name: onAutoFillCancel;
  }
  @keyframes onAutoFillStart {
    from {/**/}
    to {/**/}
  }

  @keyframes onAutoFillCancel {
    from {/**/}
    to {/**/}
  }

</style>
